
import { Vue, Component, Prop } from "vue-property-decorator";
import Icon from "@/components/UI/Icon.vue";
import {
  getDocumentUrlFromId,
  replaceDocumentWithAnother
} from "@/lib/wordpress/load";
import EditTemplateModal from "@/subapps/manage-templates/EditTemplateModal.vue";
import {
  SupportedPostType,
  Template,
  TemplatesManagementData
} from "@/subapps/manage-templates/types";
import { DocumentMeta } from "@/types/document";
import { configStore, documentStore } from "@/store/store";
import { pipe } from "fp-ts/function";
import {
  getAllGlobalBlocksFromWordPress,
  getAllTemplatesAndNeededDataFromWordPress
} from "@/subapps/manage-templates/lib/api";
import { fold } from "fp-ts/Either";
import NotificationLogger from "@/lib/notifications/notifications";
import EditGlobalBlockModal from "@/subapps/manage-templates/EditGlobalBlockModal.vue";
import { GlobalBlock } from "@/types/global-blocks";

@Component({
  components: { EditGlobalBlockModal, EditTemplateModal, Icon }
})
export default class DocumentChooserItem extends Vue {
  @Prop()
  item!: DocumentMeta;

  @Prop({ default: false })
  highlight!: boolean;

  templates: Template[] = [];
  globalBlocks: GlobalBlock[] = [];

  modalData: { type: "edit" | "add"; data: Template | GlobalBlock } | false =
    false;
  loadingData = false;

  templatesManagementData: TemplatesManagementData = {
    conditions: [],
    dynamicFields: [],
    templateTypeCategories: [],
    popupTriggers: null
  };

  get currentDocument() {
    return documentStore.document;
  }

  get templateParentsWithoutTrashed() {
    return [];
  }

  get templatesByStatus() {
    return [];
  }

  get documentUrl() {
    return getDocumentUrlFromId(this.item.id);
  }

  get isBreakdanceTemplate() {
    return this.item.postType.startsWith("breakdance_");
  }

  get currentTemplate() {
    return this.templates.find(t => t.id === this.item.id);
  }

  get currentGlobalBlock() {
    return this.globalBlocks.find(t => t.id === this.item.id);
  }

  get isGlobalBlockTemplate() {
    return this.item.postType === "breakdance_block";
  }

  async editGlobalBlock() {
    this.loadingData = true;

    await this.loadGlobalBlocks();

    if (!this.currentGlobalBlock) {
      NotificationLogger.log.message("Couldn't load Global Blocks data");
      return;
    }

    this.modalData = {
      type: "edit",
      data: this.currentGlobalBlock
    };

    this.loadingData = false;
  }

  async editTemplate() {
    this.loadingData = true;

    await this.loadTemplates();

    if (!this.currentTemplate) {
      NotificationLogger.log.message("Couldn't load templates data");
      return;
    }

    this.modalData = {
      type: "edit",
      data: this.currentTemplate
    };

    this.loadingData = false;
  }

  async loadGlobalBlocks() {
    pipe(
      await getAllGlobalBlocksFromWordPress(),
      fold(
        () => {
          NotificationLogger.log.message("Couldn't load Global Blocks data");
        },
        blocks => {
          this.globalBlocks = blocks;
        }
      )
    );
  }

  async loadTemplates() {
    const type = this.item.postType as SupportedPostType;

    pipe(
      await getAllTemplatesAndNeededDataFromWordPress(type),
      fold(
        () => {
          NotificationLogger.log.message("Couldn't load templates data");
        },
        data => {
          this.templates = data.templates;
          this.templatesManagementData = data.templatesManagementData;
          configStore.setDynamicFields(
            data.templatesManagementData.dynamicFields
          );
          configStore.setConditions(data.templatesManagementData.conditions);
          configStore.setEnvironment(data.environment);
        }
      )
    );
  }

  async replaceCurrentBuilderPage(event: KeyboardEvent) {
    if (event.ctrlKey || event.metaKey) return;

    if (window.Breakdance.initialMode !== "builder") return;

    event.preventDefault();

    this.$emit("click");
    replaceDocumentWithAnother(this.item.id);
  }
}
